import { HALLIDAY_PAYMENTS_API_URL } from "@shared/constants";

import {
    PaymentSession,
    PaymentsWidgetSDKParams,
    SDKOverrides,
    WidgetLoadError,
    WidgetLoadFailureReason,
    WindowType,
} from "../types";
import { getPaymentsWidgetUrl } from "../utils";

import {
    getState,
    setState,
    setReactiveState,
    getLastMergedConfig,
    getProviderWindowRef,
    isPreloaded,
    mergeAndStoreConfig,
    serializeParams,
    hasParamsChanged,
    setLastParamHash,
    bumpGeneration,
    getGeneration,
    type WidgetContext,
} from "./state";
import {
    validateParams,
    validateOverrides,
    computeWindowOrigin,
    computeSDKAdditions,
    remapOverrides,
    resolveWalletMeta,
    type FunderRegistry,
    type ResolvedWalletMeta,
} from "./validateAndPrepare";
import type { NormalizedConfig } from "./normalizeConfig";
import { getOrCreateIframe } from "./iframe";
import { monitorIframeLoad } from "./iframeLoadMonitor";
import { setupMessageHandlers, sendUpdateParams } from "./messaging";
import { hideWidget, showWidget } from "./display";
import { removeEmbedSkeleton } from "./skeleton";

type PipelineOptions = { show?: boolean | undefined; session?: PaymentSession | undefined };

/**
 * Async orchestrator ("caller step"): validate → bump generation → resolve wallet meta →
 * discard if superseded → run the synchronous pipeline.
 *
 * Wallet resolution is the only async work. Once it completes for the *latest* generation, the
 * synchronous {@link executePipeline} runs with no further `await`, so iframe creation and handler
 * registration happen atomically — there is no async gap a stale resolution could slip through.
 *
 * Internal/testing overrides arrive on the typed `dangerousOverrides` config key (validated on its own
 * path, mirroring `validateOverrides`). The legacy positional channel (`args[999]`/`args[0]`) is gone.
 */
export const runPipeline = async (
    params: NormalizedConfig,
    { show = false, session }: PipelineOptions = {},
    ctx: WidgetContext,
): Promise<void> => {
    // 1. Validate params + overrides, then resolve owner shorthand. Any throw propagates to the caller's
    // `.catch(err => _reportError(err, "<source>"))`, the SINGLE error-reporting channel — runPipeline
    // never reports on its own (internal-only; every caller catches). Overrides come from the typed
    // `dangerousOverrides` key (stripped from `validatedParams` by the flat schema, so they never leak
    // into the widget URL).
    const validatedParams = validateParams(params as PaymentsWidgetSDKParams);
    const validatedOverrides = validateOverrides(params.dangerousOverrides ?? {});

    // Tag this entry point with a fresh generation, then resolve wallet addresses.
    const generation = bumpGeneration(ctx);

    // 1b. Resolve all wallet addresses in parallel (skip if no roles to resolve)
    const hasRoles =
        validatedParams.owner ||
        validatedParams.funder ||
        validatedParams.funders?.length ||
        validatedParams.withdrawFunder;
    const resolved: ResolvedWalletMeta = hasRoles
        ? await resolveWalletMeta(validatedParams)
        : { ownerMeta: undefined, funderRegistry: new Map() as FunderRegistry, funderMeta: [] };

    // Latest-call-wins: a newer entry point or destroy() bumped the generation while we awaited.
    // Discard the stale resolution before it can build an iframe or register handlers.
    if (generation !== getGeneration(ctx)) return;

    // 2-13. Build + display synchronously (no `await` past this point).
    executePipeline(validatedParams, validatedOverrides, resolved, { show, session }, ctx);
};

/**
 * Synchronous pipeline core (steps 2–13). Receives pre-resolved wallet meta and contains NO `await`,
 * so message handlers register synchronously and fire later. Stale resolutions are filtered by the
 * generation guard in {@link runPipeline} before they reach here.
 */
export const executePipeline = (
    validatedParams: PaymentsWidgetSDKParams,
    validatedOverrides: SDKOverrides,
    resolved: ResolvedWalletMeta,
    { show = false, session }: PipelineOptions = {},
    ctx: WidgetContext,
): void => {
    const { ownerMeta, funderRegistry, funderMeta } = resolved;

    // 2. Build unified widget config (used for both URL generation and config storage)
    const windowType: WindowType = validatedParams.targetElementId ? "EMBED" : "MODAL";
    const windowOrigin = computeWindowOrigin(validatedOverrides);
    setState({ windowOrigin }, ctx);
    const sdkAdditions = computeSDKAdditions(validatedParams, windowType, validatedOverrides, {
        ownerMeta,
        funderMeta,
    });
    const shortOverrides = remapOverrides(validatedOverrides);

    const widgetConfig = {
        ...validatedParams,
        sandbox: validatedParams.sandbox || false,
        windowOrigin,
        ...sdkAdditions,
        ...shortOverrides,
        // Written unconditionally (explicit `undefined` when session-less) so the merged config
        // self-clears a prior open's session rather than carrying it forward (state.ts merges via spread).
        session,
        show,
    };

    const { hasOwner, hasTxHandler } = sdkAdditions;

    const widgetUrl = getPaymentsWidgetUrl(widgetConfig);

    // 3. Get or create iframe
    const { iframe, reused } = getOrCreateIframe(widgetUrl, ctx);

    const getContentWindow = (): Window | null => iframe?.contentWindow ?? null;

    // 4. Config merge & change detection
    const fullConfig = mergeAndStoreConfig(widgetConfig as Record<string, unknown>, ctx);

    // 5-6. Param change detection
    const paramHash = serializeParams(fullConfig);
    const paramsChanged = hasParamsChanged(paramHash, ctx);

    // 7. Early return: same iframe/popup, same params, not showing.
    // NOTE (deferred, finding #2): a same-address role swap that changes only function bodies serializes to
    // identical params, so this intentionally skips the re-run. No behavior change — see Non-Goals.
    if (reused && !paramsChanged && !show) {
        if (isPreloaded(ctx) && validatedParams.onReady) validatedParams.onReady();
        return;
    }

    // 8. Store hash
    setLastParamHash(paramHash, ctx);

    // 9. Reused with changed params → send update (or defer if widget not ready). A session is
    // force-sent even when params are otherwise unchanged (its delivery is the point of the open).
    if ((reused && paramsChanged) || session) {
        if (isPreloaded(ctx)) {
            sendUpdateParams(getContentWindow(), fullConfig, windowOrigin);
        } else {
            setState({ pendingUpdateParams: { config: fullConfig, windowOrigin } }, ctx);
        }
    }

    // 10. New iframe → store in state, create load monitor, wire onerror
    if (!reused && iframe) {
        setState({ iframe, isPreloaded: false }, ctx);
        // Defensive: a freshly built iframe is not ready until its own WIDGET_READY, so reactive
        // isReady must report false during the rebuild. (The primary reset is synchronous in
        // HallidayPayments.updateConfig; this backstops any other entry that builds a new iframe.)
        setReactiveState({ isReady: false }, ctx);

        const monitor = monitorIframeLoad({
            iframe,
            expectedOrigin: windowOrigin,
            onError: (error) => {
                const configOnError = getLastMergedConfig(ctx)?.onError as ((error: Error) => void) | undefined;
                configOnError?.(error);
            },
        });
        setState({ iframeLoadMonitor: monitor }, ctx);

        // Enrich iframe.onerror with diagnostics
        iframe.onerror = () => {
            const richError =
                monitor.diagnose() ??
                new WidgetLoadError("Widget load failed", WidgetLoadFailureReason.UNKNOWN, {
                    onloadFired: false,
                    cspViolationDetected: false,
                    cspBlockedURI: null,
                    iframeInDOM: true,
                    resourceTimingEntryExists: false,
                    elapsedMs: 0,
                });
            const configOnError = getLastMergedConfig(ctx)?.onError as ((error: Error) => void) | undefined;
            configOnError?.(richError);
        };
    }

    // 11. Handlers — always clean+register, config-driven
    // Owner is a discriminated union: signMessage/signTypedData live only on the direct arm; the
    // wallet-auth arm carries signAuthMessage; otp-auth carries no signer. Narrow before reading them
    // (matches computeSDKAdditions / resolveWalletMeta).
    const owner = validatedParams.owner;
    const directOwner = owner && (!owner.type || owner.type === "direct") ? owner : undefined;
    setupMessageHandlers(
        {
            getContentWindow,
            windowOrigin,
            providerWindowRef: getProviderWindowRef(ctx),
            hasOwner,
            hasTxHandler,
            signMessage: directOwner?.signMessage,
            signTypedData: directOwner?.signTypedData,
            signAuthMessage: owner?.type === "wallet-auth" ? owner.signAuthMessage : undefined,
            funderRegistry,
            connect: validatedParams.onConnectWallet,
            generateFeeSponsorshipGrant: validatedParams.generateFeeSponsorshipGrant,
            statusCallback: validatedParams.onStatus,
            notificationsCallback: validatedParams.onNotifications,
            apiKey: validatedParams.apiKey,
            apiBaseUrl: shortOverrides.apiBaseUrl || HALLIDAY_PAYMENTS_API_URL,
            onReady: () => {
                setState({ isPreloaded: true }, ctx);
                // Destroy monitor on success
                getState(ctx).iframeLoadMonitor?.destroy();
                setState({ iframeLoadMonitor: null }, ctx);
                // Remove EMBED skeleton on widget ready
                removeEmbedSkeleton(ctx);
                // Flush any buffered deferred update; if none, push current merged config for widget-reload re-sync.
                const pending = getState(ctx).pendingUpdateParams;
                const config = pending?.config ?? getLastMergedConfig(ctx);
                if (config) {
                    sendUpdateParams(getContentWindow(), config, windowOrigin);
                }
                setState({ pendingUpdateParams: null }, ctx);
                validatedParams.onReady?.();
            },
            onError: validatedParams.onError,
            onClose: () => hideWidget(ctx),
        },
        ctx,
    );

    // 12. Display (show=true only)
    if (show) {
        showWidget(
            {
                windowType,
                targetElementId: validatedParams.targetElementId,
                windowOrigin,
                customStyles: validatedParams.customStyles,
            },
            { widgetUrl },
            ctx,
        );
    }

    // 13. Already-ready notification (reused iframe/popup that was preloaded)
    if (reused && isPreloaded(ctx) && validatedParams.onReady) {
        validatedParams.onReady();
    }
};
